import React from 'react';
import _ from 'underscore';

import {MakeFieldsOptional} from 'util/Types';

import * as LegacyModalContainer from './LegacyModalContainer';

interface ModalProps<T> {
  onRequestSubmit?(result: T): void | Promise<void>;
  onRequestClose(): void;
  modalKey?: string;
}

export type ModalReturnType<P> = P extends ModalProps<infer T> ? T : void;

/**
 * @deprecated ⚠️ Use `runModal` instead to render a Mantine Modal imperatively.
 *
 * To migrate an existing legacyRunModal call to use the new Mantine-backed UI Library Modals, you need to:
 * 1. Change the import from `legacyRunModal` to `runModal.
 * 2. Change the Modal component to not actually include the Modal UI element
 * 3. Replace the `onRequestSubmit` and `onRequestClose` props with a single `close` prop that can be called
 *    to close the modal and resolve the Promise with an optional result.
 * 4. Move the title and other modal-level props to the `containerProps` parameter.
 * 5. Include ModalFooter and Buttons if previously using the legacy Modal buttons prop
 *
 * Open a modal and return a promise that resolves with the modal's "return value" when the modal
 * finishes.
 *
 * The modal is assumed to have a onRequestSubmit and onRequestClose prop that, when called, should
 * close the modal. If onRequestSubmit is called, its argument is used as the resolved value of the
 * promise. If onRequestClose is called, null is used as the return value of the promise.
 *
 * An optional modalKey value can be passed in to allow for closing the modal outside of onRequestSubmit/onRequestClose.
 */
export default function legacyRunModal<P extends ModalProps<unknown>>(
  ModalComponent: React.ComponentType<P>,
  props: MakeFieldsOptional<P, 'onRequestSubmit' | 'onRequestClose' | 'modalKey'>
): Promise<ModalReturnType<P> | null> {
  return new Promise((resolve) => {
    const modalKey = props.modalKey ?? _.uniqueId('run-modal');
    LegacyModalContainer.open(
      modalKey,
      <ModalComponent
        // TS isn't smart enough to figure out that excluding and re-adding onRequestSubmit and
        // onRequestClose works, so do an unsafe cast to P to work around.
        {...(props as P)}
        onRequestSubmit={async (result: ModalReturnType<P>) => {
          if (props.onRequestSubmit) {
            await props.onRequestSubmit(result);
          }
          LegacyModalContainer.close(modalKey);
          resolve(result);
        }}
        onRequestClose={() => {
          if (props.onRequestClose) {
            props.onRequestClose();
          }
          LegacyModalContainer.close(modalKey);
          resolve(null);
        }}
      />
    );
  });
}
